/**
 * User-level voice preferences. Sit on top of the channel's bitrate /
 * processing mode (which voiceMedia.ts decides from the channel config)
 * and override the per-track constraints + device selection on this
 * device.
 *
 * Persisted to localStorage so the user picks their headset once and
 * the same device is used on every voice session. Cross-device sync is
 * intentionally out of scope — input/output device IDs are valid for
 * one physical machine only.
 */

/** Desired frame rate for screen share capture (fps). 15 = low-bandwidth/
 *  code-review tier; 30 = default smooth motion; 60 = fast-motion content
 *  (gameplay, animation) — costs ~2x CPU + uplink. */
export type ScreenFps = 15 | 30 | 60;

/**
 * Resolution cap for screen share. 'source' means capture at the native
 * display resolution (let the browser / OS decide) — no ideal width/height
 * constraints are sent to getDisplayMedia, which is the browser default.
 */
export type ScreenResolution = '720p' | '1080p' | '1440p' | '4k' | 'source';

/**
 * Hint passed as the `displaySurface` constraint to getDisplayMedia.
 * 'any' = browser decides (default OS picker with no pre-selection);
 * 'monitor', 'window', 'browser' = hint to the browser to pre-select that
 * surface type in the picker. The browser is free to ignore it — this is
 * advisory only, not a filter.
 */
export type ScreenSurface = 'any' | 'monitor' | 'window' | 'browser';

export interface VoicePreferences {
  /** deviceId of the chosen microphone, or 'default' for the OS default. */
  inputDeviceId: string;
  /** deviceId of the chosen output, or 'default'. NOT honored everywhere —
   * `setSinkId` is Chromium-only; on Firefox / Safari we fall through to
   * the system default and the picker stays informational. */
  outputDeviceId: string;
  /** WebRTC processing flags. Authoritative on every channel — the user's
   * choice is requested as-is regardless of the channel's bitrate/mode, so
   * noise suppression stays on at 128+ kbps when the toggle is on. */
  /** Acoustic echo cancellation (browser). Kept as a toggle — neither our
   * DSP filter nor RNNoise cancel echo (that needs the speaker reference
   * signal, which only the browser AEC has). */
  echoCancellation: boolean;
  /** «Как в бункере» — our proprietary DSP filter (high-pass + noise gate +
   * compressor + limiter). Replaces the browser noise-suppression and
   * auto-gain (we force those off so they don't double-process). */
  bunkerFilter: boolean;
  /** Нейрошумодав — RNNoise (ML noise suppression), applied client-side in the
   * same processing graph. Independent toggle; can stack with bunkerFilter. */
  neuralSuppression: boolean;
  /** Премиум-нейрошумодав — DeepFilterNet (heavier ML, ~16MB lazy-loaded on
   * first enable). Independent toggle. */
  deepFilter: boolean;
  /** DeepFilterNet noise-suppression intensity, expressed as the model's
   * attenuation limit in dB (0..100). 100 = maximum removal (can sound
   * over-processed / «underwater» on some voices); lower = gentler, more
   * natural, leaves a little ambient. Live-tunable without a graph rebuild. */
  deepFilterAtten: number;
  /** Mic transmission mode: 'open' = always transmit; 'ptt' = transmit only
   * while the push-to-talk key is held; 'voice' = transmit only while speaking. */
  micMode: 'open' | 'ptt' | 'voice';
  /** Push-to-talk key — a KeyboardEvent.code (layout-independent), e.g.
   * 'KeyT', 'Space', 'ControlLeft'. Empty = not assigned. */
  pttKey: string;
  /** Voice-activation sensitivity, 0-100. Higher = opens on quieter speech
   * (lower RMS threshold). Only used when micMode === 'voice'. */
  vadSensitivity: number;
  /** 0-200, applied as <audio>.volume = value/100. >100 boosts via Web Audio
   * GainNode in VoiceAudioLayer. Default 100 = system volume. */
  outputGain: number;
  /** 0-200, applied to the microphone track before it is sent to the SFU.
   * 100 is unchanged, 0 is silent and 200 doubles the signal amplitude. */
  inputGain: number;

  // ── Screen share ──────────────────────────────────────────────────────────
  /** Target frame rate for screen share capture. Lower = less CPU + bandwidth;
   *  higher = smoother for fast-moving content like games. 30 is the default
   *  — smooth for most presentations and light video; drop to 15 on a
   *  throttled uplink, bump to 60 for gameplay. */
  screenFps: ScreenFps;
  /** Resolution cap for the captured screen stream. 'source' means no ideal
   *  constraint (native resolution). Lower values reduce encoder bitrate and
   *  downstream decode cost on slower machines. */
  screenResolution: ScreenResolution;
  /** Advisory hint for the getDisplayMedia surface picker. 'any' lets the OS
   *  show its default combined picker; 'monitor'/'window'/'browser' pre-selects
   *  that tab in Chromium's built-in picker (ignored by Firefox/Safari). */
  screenSurface: ScreenSurface;
  /** Always use system audio fallback for window screen shares. */
  screenAudioFallback: boolean;
  /** Adaptive screen-share quality: when the uplink/CPU can't sustain the
   *  chosen tier, step down the quality ladder automatically (and back up
   *  once the connection recovers). The chosen tier stays the ceiling. */
  screenQualityAuto: boolean;
}

const STORAGE_KEY = 'bunker.voicePreferences.v1';

const DEFAULTS: VoicePreferences = {
  inputDeviceId: 'default',
  outputDeviceId: 'default',
  // New users get every filter ON by default EXCEPT the experimental
  // (DeepFilterNet, ~16MB lazy-load) — echo cancellation + «Фильтр улучшения
  // качества» (bunkerFilter) + «Как в бункере» (neuralSuppression/RNNoise) are
  // all enabled; the experimental deepFilter stays opt-in.
  echoCancellation: true,
  bunkerFilter: true,
  neuralSuppression: true,
  deepFilter: false,
  deepFilterAtten: 100,
  micMode: 'open',
  pttKey: '',
  vadSensitivity: 55,
  outputGain: 100,
  inputGain: 100,
  // Screen share defaults: 30fps / 1080p / any surface — Discord-equivalent
  // baseline. Users can bump to 60fps for gameplay/video or drop to 15fps to
  // save uplink on a tethered/throttled connection.
  screenFps: 30,
  screenResolution: '1080p',
  screenSurface: 'any',
  screenAudioFallback: false,
  // Adaptive degradation only ever lowers quality below the user's pick under
  // congestion — safe to have on for everyone by default.
  screenQualityAuto: true,
};

let cache: VoicePreferences | null = null;
const listeners = new Set<() => void>();

const SCREEN_FPS_VALUES: readonly ScreenFps[] = [15, 30, 60];
const SCREEN_RESOLUTION_VALUES: readonly ScreenResolution[] = ['720p', '1080p', '1440p', '4k', 'source'];
const SCREEN_SURFACE_VALUES: readonly ScreenSurface[] = ['any', 'monitor', 'window', 'browser'];
const MIC_MODE_VALUES: readonly VoicePreferences['micMode'][] = ['open', 'ptt', 'voice'];

/**
 * Coerce a parsed/merged prefs object to valid values. Stored prefs come from
 * localStorage — treat them as untrusted. A stale schema, a hand-edited value,
 * or a future enum change must not flow a bad `screenResolution`/`screenFps`
 * into the getDisplayMedia constraints (which would produce `height:{ideal:
 * undefined}` / `width:{ideal:NaN}`), nor a non-numeric value into the gain /
 * sensitivity math. Every field falls back to its default when invalid.
 */
export function sanitizeVoicePreferences(p: Partial<VoicePreferences>): VoicePreferences {
  const oneOf = <T>(val: unknown, allowed: readonly T[], fallback: T): T =>
    (allowed as readonly unknown[]).includes(val) ? (val as T) : fallback;
  const num = (val: unknown, fallback: number, min: number, max: number): number =>
    typeof val === 'number' && Number.isFinite(val) ? Math.min(max, Math.max(min, val)) : fallback;
  const bool = (val: unknown, fallback: boolean): boolean => (typeof val === 'boolean' ? val : fallback);
  const str = (val: unknown, fallback: string): string => (typeof val === 'string' ? val : fallback);
  return {
    inputDeviceId: str(p.inputDeviceId, DEFAULTS.inputDeviceId),
    outputDeviceId: str(p.outputDeviceId, DEFAULTS.outputDeviceId),
    echoCancellation: bool(p.echoCancellation, DEFAULTS.echoCancellation),
    bunkerFilter: bool(p.bunkerFilter, DEFAULTS.bunkerFilter),
    neuralSuppression: bool(p.neuralSuppression, DEFAULTS.neuralSuppression),
    deepFilter: bool(p.deepFilter, DEFAULTS.deepFilter),
    deepFilterAtten: num(p.deepFilterAtten, DEFAULTS.deepFilterAtten, 0, 100),
    micMode: oneOf(p.micMode, MIC_MODE_VALUES, DEFAULTS.micMode),
    pttKey: str(p.pttKey, DEFAULTS.pttKey),
    vadSensitivity: num(p.vadSensitivity, DEFAULTS.vadSensitivity, 0, 100),
    outputGain: num(p.outputGain, DEFAULTS.outputGain, 0, 200),
    inputGain: num(p.inputGain, DEFAULTS.inputGain, 0, 200),
    screenFps: oneOf(p.screenFps, SCREEN_FPS_VALUES, DEFAULTS.screenFps),
    screenResolution: oneOf(p.screenResolution, SCREEN_RESOLUTION_VALUES, DEFAULTS.screenResolution),
    screenSurface: oneOf(p.screenSurface, SCREEN_SURFACE_VALUES, DEFAULTS.screenSurface),
    screenAudioFallback: bool(p.screenAudioFallback, DEFAULTS.screenAudioFallback),
    screenQualityAuto: bool(p.screenQualityAuto, DEFAULTS.screenQualityAuto),
  };
}

function load(): VoicePreferences {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { ...DEFAULTS };
    const parsed = JSON.parse(raw) as Partial<VoicePreferences>;
    return sanitizeVoicePreferences(parsed);
  } catch {
    return { ...DEFAULTS };
  }
}

function save(next: VoicePreferences) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    /* private mode — in-memory only */
  }
}

export function getVoicePreferences(): VoicePreferences {
  if (!cache) cache = load();
  return cache;
}

export function updateVoicePreferences(patch: Partial<VoicePreferences>) {
  const next = { ...getVoicePreferences(), ...patch };
  cache = next;
  save(next);
  for (const fn of listeners) fn();
}

export function subscribeVoicePreferences(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/**
 * RMS amplitude (0..1) at which voice-activation opens the mic. Derived from
 * `vadSensitivity` — higher sensitivity → lower threshold (opens on quieter
 * speech). At sensitivity 55 this is ≈0.07, matching the speaking indicator.
 */
export function vadThresholdRms(): number {
  // Gentle curve: even mid-sensitivity must open on normal/quiet speech
  // (~0.03–0.05 RMS). The previous 0.16 max was far too high — below 100%
  // sensitivity it never crossed, so the mic stayed shut ("звук пропадает").
  return Math.max(0.004, ((100 - getVoicePreferences().vadSensitivity) / 100) * 0.05);
}

/** Pixel heights for each named resolution tier. */
const RESOLUTION_HEIGHT: Record<Exclude<ScreenResolution, 'source'>, number> = {
  '720p': 720,
  '1080p': 1080,
  '1440p': 1440,
  '4k': 2160,
};

/** Per-resolution target bitrate for screen share at 30fps (bps). Tuned for
 *  `contentHint:'detail'` screen content — crisp text/UI/gameplay without
 *  starving the encoder. Scaled by the fps factor below. */
const SCREEN_BASE_BITRATE: Record<ScreenResolution, number> = {
  '720p': 2_000_000,
  '1080p': 4_000_000,
  '1440p': 8_000_000,
  '4k': 16_000_000,
  // 'source' = native resolution, unknown until capture. Assume a 1440p-class
  // display (the common high-DPI case) so the producer isn't starved.
  source: 10_000_000,
};

/** fps → bitrate multiplier. 60fps doesn't need a full 2× (the detail hint +
 *  static screen regions inter-frame-compress well); 15fps saves uplink. */
const SCREEN_FPS_FACTOR: Record<ScreenFps, number> = {
  15: 0.7,
  30: 1,
  60: 1.5,
};

/** Hard ceiling so an extreme tier (e.g. 4k60) can't blow up the sharer's
 *  uplink or the SFU forward. */
const SCREEN_BITRATE_CEILING = 16_000_000;

/**
 * Target max bitrate (bps) for the screen-share video producer, scaled to the
 * chosen resolution + fps.
 *
 * Why: the producer previously used a flat 4 Mbps tuned for 1080p30. Once the
 * quality picker let users select 1440p/60, that flat cap starved the higher
 * tiers — a 1440p60 capture squeezed into 4 Mbps looks blocky/mushy. Keeping
 * bitrate proportional to pixels·fps lets the picked tier actually look crisp.
 *
 * NOTE: this does NOT reduce the CPU/encode cost of a high tier — capturing &
 * encoding 1440p60 is inherently ~3-4× heavier than 1080p30. That load is the
 * cost of the user's choice; this only fixes the visual quality of the result.
 */
export function screenMaxBitrate(resolution: ScreenResolution, fps: ScreenFps): number {
  const base = SCREEN_BASE_BITRATE[resolution] ?? SCREEN_BASE_BITRATE['1080p'];
  const factor = SCREEN_FPS_FACTOR[fps] ?? 1;
  return Math.min(SCREEN_BITRATE_CEILING, Math.round(base * factor));
}

/**
 * Build the `video` constraint block for navigator.mediaDevices.getDisplayMedia
 * from the user's current screen-share preferences.
 *
 * The returned object is safe to spread directly into the `video` field of
 * getDisplayMedia options. Unknown / unsupported constraints are ignored by
 * the browser, so it's fine to always include `displaySurface` even on
 * Firefox/Safari where it has no effect.
 *
 * Resolution constraint strategy:
 *   - 'source' → no ideal width/height (browser picks native resolution).
 *   - Any other tier → `height: { ideal: N }` cap. We constrain height rather
 *     than width because landscape and portrait sources differ on width but
 *     height stays the canonical dimension for resolution tiers. We use
 *     `ideal` (not `max`) so the browser can still negotiate a larger
 *     resolution if the source only supports it — a 4K monitor will still
 *     be captured at 4K when the user picks 1080p ideal and the source only
 *     offers 4K, but the encoder will downscale in the produce step.
 *     NOTE: If you want a hard cap (e.g. to save bandwidth), switch `ideal`
 *     to `max` — but this risks capture failure on sources that don't support
 *     lower-res negotiation.
 */
export function buildScreenShareConstraints(): MediaTrackConstraints {
  const prefs = getVoicePreferences();
  const constraints: MediaTrackConstraints = {
    frameRate: { ideal: prefs.screenFps, max: prefs.screenFps },
  };
  if (prefs.screenResolution !== 'source') {
    const h = RESOLUTION_HEIGHT[prefs.screenResolution];
    constraints.height = { ideal: h };
    // Width is set proportionally for 16:9. At 4k this is 3840, at 1440p
    // it is 2560, etc. This avoids letter-boxing on widescreen sources.
    constraints.width = { ideal: Math.round(h * (16 / 9)) };
  }
  if (prefs.screenSurface !== 'any') {
    // `displaySurface` is a DisplayCaptureSurfaceType in the spec. It's a
    // constraint passed inside the `video` block — NOT a top-level option.
    // MediaTrackConstraints doesn't include it in the lib.dom types, so we
    // cast via the index signature; the browser validates it at runtime.
    (constraints as Record<string, unknown>)['displaySurface'] = prefs.screenSurface;
  }
  return constraints;
}

/**
 * Apply the user's mic constraints on top of the channel constraints.
 *
 * Browser noise-suppression and auto-gain are forced OFF: «Как в бункере»
 * (our DSP filter, on by default) does that job in the WebAudio graph, and
 * stacking the browser's versions on top double-processes the signal
 * (watery denoise, pumping gain). Echo cancellation stays user-controlled —
 * nothing else cancels echo. The channel mode only governs fidelity (sample
 * rate / channel count / Opus).
 */
export function applyUserVoicePrefs(
  channelConstraints: MediaTrackConstraints,
): MediaTrackConstraints {
  const prefs = getVoicePreferences();
  const merged: MediaTrackConstraints = {
    ...channelConstraints,
    echoCancellation: prefs.echoCancellation,
    noiseSuppression: false,
    autoGainControl: false,
  };
  if (prefs.inputDeviceId && prefs.inputDeviceId !== 'default') {
    merged.deviceId = { exact: prefs.inputDeviceId };
  }
  return merged;
}
